import React, { useEffect, useState } from "react";
import { Modal } from "@mui/material";
import Styles from "./FinalEditModal.module.css";
import { Close } from "@mui/icons-material";
import TimeInput from "../../../../commons/Inputs/TimeInput/TimeInput";
import TextInput from "../../../../commons/Inputs/TextInput/TextInput";
import { ReactComponent as DeleteIcon } from "../../../../assets/images/RemoveIcon.svg";
import { ReactComponent as AddIcon } from "../../../../assets/images/AdditionIcon.svg";
import DateInput from "../../../../commons/Inputs/DateInput/DateInput";
import { toast } from "react-toastify";

const formatDateToDDMMYYYY = (date) => {
  if (!date) return "";
  const dateObj = date instanceof Date ? date : new Date(date);
  if (isNaN(dateObj.getTime())) return "";
  const day = String(dateObj.getDate()).padStart(2, "0");
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = monthNames[dateObj.getMonth()];
  const year = dateObj.getFullYear();
  return `${day}-${month}-${year}`;
};

const parseDateString = (dateStr) => {
  if (!dateStr) return null;

  // Handle dd-MM-yyyy format (e.g., "02-02-2026")
  if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
    const [day, month, year] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  // Handle dd-MMM-yyyy format (e.g., "02-Feb-2026")
  if (/^\d{2}-[A-Za-z]{3}-\d{4}/.test(dateStr)) {
    const [day, monthStr, year] = dateStr.split("-");
    const monthMap = {
      Jan: 0,
      Feb: 1,
      Mar: 2,
      Apr: 3,
      May: 4,
      Jun: 5,
      Jul: 6,
      Aug: 7,
      Sep: 8,
      Oct: 9,
      Nov: 10,
      Dec: 11,
    };
    const month = monthMap[monthStr];
    if (month !== undefined) {
      return new Date(Number(year), month, Number(day));
    }
  }

  // Try parsing as ISO date or other formats
  const parsed = new Date(dateStr);
  return isNaN(parsed.getTime()) ? null : parsed;
};

const timeToMinutes = (time) => {
  if (!time) return null;
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
};

const isTimeValid = (start, end) => {
  if (!start || !end) return false;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  if (isNaN(sh) || isNaN(sm) || isNaN(eh) || isNaN(em)) return false;
  return eh > sh || (eh === sh && em > sm);
};

const clearRowError = (errorsArr = [], index, field) => {
  const next = [...errorsArr];
  if (next[index]) {
    next[index] = { ...next[index] };
    delete next[index][field];
  }
  return next;
};

const clearFieldError = (errorsState, field) => ({
  ...errorsState,
  [field]: "",
});

const checkDateOverlap = (startDate1, endDate1, startDate2, endDate2) => {
  if (!startDate1 || !endDate1 || !startDate2 || !endDate2) return false;

  const start1 = new Date(startDate1);
  const end1 = new Date(endDate1);
  const start2 = new Date(startDate2);
  const end2 = new Date(endDate2);
  return start1 <= end2 && start2 <= end1;
};

const getDateRangeArray = (startDate, endDate) => {
  const dates = [];
  const start = new Date(startDate);
  const end = new Date(endDate);

  const current = new Date(start);
  while (current <= end) {
    dates.push(current.toISOString().split("T")[0]);
    current.setDate(current.getDate() + 1);
  }
  return dates;
};

const validateInjectionDrawalSlotTimeOverlaps = (injectionRows, drawalRows) => {
  const overlaps = [];

  injectionRows.forEach((injectionSlot, injectSlotIndex) => {
    if (
      !injectionSlot.startDate ||
      !injectionSlot.endDate ||
      !injectionSlot.startTime ||
      !injectionSlot.endTime
    ) {
      return;
    }

    const injectionDates = getDateRangeArray(
      injectionSlot.startDate,
      injectionSlot.endDate,
    );
    const injStartTime = timeToMinutes(injectionSlot.startTime);
    const injEndTime = timeToMinutes(injectionSlot.endTime);

    drawalRows.forEach((drawalSlot, drawalSlotIndex) => {
      if (
        !drawalSlot.startDate ||
        !drawalSlot.endDate ||
        !drawalSlot.startTime ||
        !drawalSlot.endTime
      ) {
        return;
      }

      const drawalDates = getDateRangeArray(
        drawalSlot.startDate,
        drawalSlot.endDate,
      );
      const drawStartTime = timeToMinutes(drawalSlot.startTime);
      const drawEndTime = timeToMinutes(drawalSlot.endTime);

      const sharedDates = injectionDates.filter((date) =>
        drawalDates.includes(date),
      );

      if (sharedDates.length > 0) {
        if (injStartTime <= drawEndTime && drawStartTime <= injEndTime) {
          sharedDates.forEach((date) => {
            overlaps.push({
              date,
              injectSlotIndex,
              drawalSlotIndex,
            });
          });
        }
      }
    });
  });

  return overlaps;
};

const validateSameTypeOverlaps = (rows, type) => {
  const overlaps = [];

  for (let i = 0; i < rows.length; i++) {
    const slot1 = rows[i];
    if (
      !slot1.dateStartAt ||
      !slot1.dateEndAt ||
      !slot1.slotStartAt ||
      !slot1.slotEndAt
    ) {
      continue;
    }

    for (let j = i + 1; j < rows.length; j++) {
      const slot2 = rows[j];
      if (
        !slot2.dateStartAt ||
        !slot2.dateEndAt ||
        !slot2.slotStartAt ||
        !slot2.slotEndAt
      ) {
        continue;
      }

      // Check if date ranges overlap
      if (
        checkDateOverlap(
          slot1.dateStartAt,
          slot1.dateEndAt,
          slot2.dateStartAt,
          slot2.dateEndAt,
        )
      ) {
        // Get shared dates
        const dates1 = getDateRangeArray(slot1.dateStartAt, slot1.dateEndAt);
        const dates2 = getDateRangeArray(slot2.dateStartAt, slot2.dateEndAt);
        const sharedDates = dates1.filter((date) => dates2.includes(date));

        if (sharedDates.length > 0) {
          // Check if times overlap on shared dates
          const time1Start = timeToMinutes(slot1.slotStartAt);
          const time1End = timeToMinutes(slot1.slotEndAt);
          const time2Start = timeToMinutes(slot2.slotStartAt);
          const time2End = timeToMinutes(slot2.slotEndAt);

          if (time1Start <= time2End && time2Start <= time1End) {
            overlaps.push({
              slotIndex1: i,
              slotIndex2: j,
              type,
            });
          }
        }
      }
    }
  }

  return overlaps;
};

const validateDateOverlaps = (injectionRows, drawalRows) => {
  const overlaps = [];

  const injectionRanges = [];
  injectionRows.forEach((slot, slotIndex) => {
    if (slot.dateStartAt && slot.dateEndAt) {
      injectionRanges.push({
        startDate: slot.dateStartAt,
        endDate: slot.dateEndAt,
        slotIndex,
        type: "injection",
      });
    }
  });

  const drawalRanges = [];
  drawalRows.forEach((slot, slotIndex) => {
    if (slot.dateStartAt && slot.dateEndAt) {
      drawalRanges.push({
        startDate: slot.dateStartAt,
        endDate: slot.dateEndAt,
        slotIndex,
        type: "drawal",
      });
    }
  });

  injectionRanges.forEach((injectionRange) => {
    drawalRanges.forEach((drawalRange) => {
      if (
        checkDateOverlap(
          injectionRange.startDate,
          injectionRange.endDate,
          drawalRange.startDate,
          drawalRange.endDate,
        )
      ) {
        overlaps.push({
          injection: injectionRange,
          drawal: drawalRange,
        });
      }
    });
  });

  return overlaps;
};

const FinalEditModal = ({
  open,
  handleClose,
  onSubmit,
  finalModalEditData,
  handleInjectionChange,
  handleDrawalChange,
  addInjectionRow,
  removeInjectionRow,
  addDrawalRow,
  removeDrawalRow,
  selfListingData,
}) => {
  const displayTime = (time) => {
    if (!time) return "";
    if (time === "23:59") return "24:00";
    return time;
  };

  const normalizeTime = (time) => {
    if (!time) return "";
    if (time === "24:00") return "23:59";
    return time;
  };

  const hasEditedBankingFactor = React.useRef(false);
  const hasEditedSettlementRate = React.useRef(false);

  const [settlementRate, setSettlementRate] = useState(
    finalModalEditData?.settlementRate || "DAM+",
  );
  const [bankingFactor, setBankingFactor] = useState(
    finalModalEditData?.bankingFactor || "",
  );
  const [expiryDate, setExpiryDate] = useState(
    finalModalEditData?.expiryDate || "",
  );
  const [expiryTime, setExpiryTime] = useState(
    finalModalEditData?.expiryTime || "",
  );
  const [termsExpiryDate, setTermsExpiryDate] = useState(
    finalModalEditData?.termsExpiryDate || "",
  );
  const [termsExpiryTime, setTermsExpiryTime] = useState(
    finalModalEditData?.termsExpiryTime || "",
  );
  const [activeType, setActiveType] = useState(
    finalModalEditData?.activeType || "GNA",
  );

  useEffect(() => {
    if (open) {
      if (!hasEditedBankingFactor.current) {
        setBankingFactor(finalModalEditData?.bankingFactor?.toString() || "");
      }
      if (!hasEditedSettlementRate.current) {
        setSettlementRate(finalModalEditData?.settlementRate || "DAM+");
      }

      setExpiryDate(finalModalEditData?.expiryDate || "");
      setExpiryTime(finalModalEditData?.expiryTime || "");
      setTermsExpiryDate(finalModalEditData?.termsExpiryDate || "");
      setTermsExpiryTime(finalModalEditData?.termsExpiryTime || "");
      setActiveType(
        finalModalEditData?.activeType === "T-GNA"
          ? "TGNA"
          : finalModalEditData?.activeType || "GNA",
      );

      if (finalModalEditData?.slotInjects) {
        finalModalEditData.slotInjects.forEach((row, index) => {
          const isRTC =
            row.slotStartAt === "00:00" &&
            (row.slotEndAt === "24:00" || row.slotEndAt === "23:59");

          if (isRTC && !row.rtcChecked) {
            setTimeout(
              () => handleInjectionChange(index, "rtcChecked", true),
              0,
            );
          }
        });
      }

      if (finalModalEditData?.slotDrawls) {
        finalModalEditData.slotDrawls.forEach((row, index) => {
          const isRTC =
            row.slotStartAt === "00:00" &&
            (row.slotEndAt === "24:00" || row.slotEndAt === "23:59");

          if (isRTC && !row.rtcChecked) {
            setTimeout(() => handleDrawalChange(index, "rtcChecked", true), 0);
          }
        });
      }
    } else {
      hasEditedBankingFactor.current = false;
      hasEditedSettlementRate.current = false;
    }
  }, [open]);

  const handleSettlementRate = (val) => {
    hasEditedSettlementRate.current = true;
    let value = String(val || "");
    if (value === "") {
      setSettlementRate("");
      return;
    }
    if (!value.startsWith("DAM")) {
      if (/^\d*\.?\d*$/.test(value)) {
        value = "DAM+" + value;
      } else {
        setSettlementRate(value);
        return;
      }
    }
    if (value === "D" || value === "DA" || value === "DAM") {
      setSettlementRate(value);
      return;
    }
    if (value.startsWith("DAM")) {
      const afterDAM = value.slice(3);

      if (afterDAM === "") {
        setSettlementRate(value);
        return;
      }
      if (afterDAM.startsWith("+") || afterDAM.startsWith("-")) {
        const sign = afterDAM[0];
        const restOfString = afterDAM.slice(1);

        if (restOfString === "") {
          setSettlementRate(value);
          return;
        }
        if (/^\d*\.?\d{0,3}$/.test(restOfString)) {
          setSettlementRate(value);
          if (restOfString && Number(restOfString) >= 1) {
            setErrors((errors) => clearFieldError(errors, "settlementRate"));
          }
          return;
        } else {
          const parts = restOfString.split(".");
          if (parts.length > 2) {
            const cleanNumbers =
              parts[0] + "." + parts.slice(1).join("").replace(/\D/g, "");
            const finalValue = cleanNumbers.includes(".")
              ? cleanNumbers.split(".")[0] +
                "." +
                cleanNumbers.split(".")[1].slice(0, 3)
              : cleanNumbers;
            value = "DAM" + sign + finalValue;
          } else if (parts.length === 2) {
            const integerPart = parts[0].replace(/\D/g, "");
            const decimalPart = parts[1].replace(/\D/g, "").slice(0, 3);
            value = "DAM" + sign + integerPart + "." + decimalPart;
          } else {
            const cleanNumbers = restOfString.replace(/\D/g, "");
            value = "DAM" + sign + cleanNumbers;
          }
          setSettlementRate(value);
          return;
        }
      } else {
        if (/^\d/.test(afterDAM)) {
          const parts = afterDAM.split(".");
          if (parts.length > 1) {
            const integerPart = parts[0].replace(/\D/g, "");
            const decimalPart = parts
              .slice(1)
              .join("")
              .replace(/\D/g, "")
              .slice(0, 3);
            value = "DAM+" + integerPart + "." + decimalPart;
          } else {
            const cleanNumbers = afterDAM.replace(/\D/g, "");
            value = "DAM+" + cleanNumbers;
          }
          setSettlementRate(value);
          return;
        } else {
          setSettlementRate(value);
          return;
        }
      }
    }
    setSettlementRate(value);
  };

  const handleBankingFactorChange = (val) => {
    hasEditedBankingFactor.current = true;
    if (/^(\d+(\.\d{0,2})?)?$/.test(val)) {
      setBankingFactor(val);
      const numVal = Number(val);
      if (!isNaN(numVal) && numVal >= 100) {
        setErrors((errors) => clearFieldError(errors, "bankingFactor"));
      }
    }
  };

  const [errors, setErrors] = useState({
    injectionRows: [],
    drawalRows: [],
    bankingFactor: "",
    settlementRate: "",
    dateOverlap: "",
  });

  const handleInjectionChangeWithValidation = (index, field, value) => {
    if (field === "requiredQty") {
      const numericValue = Number(value);
      if (numericValue < 0 || numericValue > 10000) {
        setErrors((prevErrors) => {
          const newErrors = { ...prevErrors };
          if (!newErrors.injectionRows) newErrors.injectionRows = [];
          if (!newErrors.injectionRows[index])
            newErrors.injectionRows[index] = {};
          newErrors.injectionRows[index][field] =
            "Quantity must be below 10,000";
          return newErrors;
        });
        return;
      } else {
        setErrors((prevErrors) => {
          const newErrors = { ...prevErrors };
          if (newErrors.injectionRows && newErrors.injectionRows[index]) {
            delete newErrors.injectionRows[index][field];
            if (Object.keys(newErrors.injectionRows[index]).length === 0) {
              newErrors.injectionRows.splice(index, 1);
            }
          }
          return newErrors;
        });
      }
      handleInjectionChange(index, field, value);
      return;
    }
    handleInjectionChange(index, field, value);

    setErrors((errors) => ({
      ...errors,
      injectionRows: clearRowError(errors.injectionRows, index, field),
    }));

    if (["slotStartAt", "slotEndAt"].includes(field)) {
      const currentRow = finalModalEditData.slotInjects[index];
      const updatedRow = { ...currentRow, [field]: value };
      const startMin = timeToMinutes(updatedRow.slotStartAt);
      const endMin = timeToMinutes(updatedRow.slotEndAt);
      if (startMin !== null && endMin !== null && endMin > startMin) {
        setErrors((errors) => ({
          ...errors,
          injectionRows: clearRowError(
            errors.injectionRows,
            index,
            "slotEndAt",
          ),
        }));
      }

      const startTime =
        field === "slotStartAt" ? value : currentRow.slotStartAt;
      const endTime = field === "slotEndAt" ? value : currentRow.slotEndAt;

      setTimeout(() => {
        const isRTC =
          startTime === "00:00" && (endTime === "24:00" || endTime === "23:59");
        const isNotRTC =
          startTime !== "00:00" || (endTime !== "24:00" && endTime !== "23:59");

        if (isRTC) {
          handleInjectionChange(index, "rtcChecked", true);
        } else if (isNotRTC) {
          handleInjectionChange(index, "rtcChecked", false);
        }
      }, 0);
    }

    if (field === "slotStartAt" && value && expiryDate && expiryTime) {
      const currentRow = finalModalEditData.slotInjects[index];
      const updatedRow = { ...currentRow, [field]: value };

      if (updatedRow.dateStartAt) {
        const startDateOnly = new Date(updatedRow.dateStartAt);
        const expiryDateOnly = new Date(expiryDate);
        startDateOnly.setHours(0, 0, 0, 0);
        expiryDateOnly.setHours(0, 0, 0, 0);

        if (startDateOnly.getTime() === expiryDateOnly.getTime()) {
          const startTimeInMinutes = timeToMinutes(value);
          const expiryTimeInMinutes = timeToMinutes(expiryTime);
          if (
            startTimeInMinutes !== null &&
            expiryTimeInMinutes !== null &&
            startTimeInMinutes <= expiryTimeInMinutes
          ) {
            setErrors((errors) => ({
              ...errors,
              injectionRows: [
                ...(errors.injectionRows || []).slice(0, index),
                {
                  ...(errors.injectionRows?.[index] || {}),
                  slotStartAt:
                    "Start Time must be 3hrs after listing expiry time",
                },
                ...(errors.injectionRows || []).slice(index + 1),
              ],
            }));
          }
        }
      }
    }

    if (field === "dateStartAt" && value && expiryDate && expiryTime) {
      const currentRow = finalModalEditData.slotInjects[index];
      const updatedRow = { ...currentRow, [field]: value };

      if (updatedRow.slotStartAt) {
        const startDateOnly = new Date(value);
        const expiryDateOnly = new Date(expiryDate);
        startDateOnly.setHours(0, 0, 0, 0);
        expiryDateOnly.setHours(0, 0, 0, 0);

        if (startDateOnly.getTime() === expiryDateOnly.getTime()) {
          const startTimeInMinutes = timeToMinutes(updatedRow.slotStartAt);
          const expiryTimeInMinutes = timeToMinutes(expiryTime);
          if (
            startTimeInMinutes !== null &&
            expiryTimeInMinutes !== null &&
            startTimeInMinutes <= expiryTimeInMinutes
          ) {
            setErrors((errors) => ({
              ...errors,
              injectionRows: [
                ...(errors.injectionRows || []).slice(0, index),
                {
                  ...(errors.injectionRows?.[index] || {}),
                  slotStartAt:
                    "Start Time must be 3hrs after listing expiry time",
                },
                ...(errors.injectionRows || []).slice(index + 1),
              ],
            }));
          }
        }
      }
    }
  };

  const handleDrawalChangeWithValidation = (index, field, value) => {
    if (field === "requiredQty") {
      const numericValue = Number(value);
      if (numericValue < 0 || numericValue > 10000) {
        setErrors((prevErrors) => {
          const newErrors = { ...prevErrors };
          if (!newErrors.drawalRows) newErrors.drawalRows = [];
          if (!newErrors.drawalRows[index]) newErrors.drawalRows[index] = {};
          newErrors.drawalRows[index][field] = "Quantity must be below 10,000";
          return newErrors;
        });
        return;
      } else {
        setErrors((prevErrors) => {
          const newErrors = { ...prevErrors };
          if (newErrors.drawalRows && newErrors.drawalRows[index]) {
            delete newErrors.drawalRows[index][field];
            if (Object.keys(newErrors.drawalRows[index]).length === 0) {
              newErrors.drawalRows.splice(index, 1);
            }
          }
          return newErrors;
        });
      }
      handleDrawalChange(index, field, value);
      return;
    }
    handleDrawalChange(index, field, value);

    setErrors((errors) => ({
      ...errors,
      drawalRows: clearRowError(errors.drawalRows, index, field),
    }));

    if (["slotStartAt", "slotEndAt"].includes(field)) {
      const currentRow = finalModalEditData.slotDrawls[index];
      const updatedRow = { ...currentRow, [field]: value };
      const startMin = timeToMinutes(updatedRow.slotStartAt);
      const endMin = timeToMinutes(updatedRow.slotEndAt);
      if (startMin !== null && endMin !== null && endMin > startMin) {
        setErrors((errors) => ({
          ...errors,
          drawalRows: clearRowError(errors.drawalRows, index, "slotEndAt"),
        }));
      }

      const startTime =
        field === "slotStartAt" ? value : currentRow.slotStartAt;
      const endTime = field === "slotEndAt" ? value : currentRow.slotEndAt;

      setTimeout(() => {
        const isRTC =
          startTime === "00:00" && (endTime === "24:00" || endTime === "23:59");
        const isNotRTC =
          startTime !== "00:00" || (endTime !== "24:00" && endTime !== "23:59");
        if (isRTC) {
          handleDrawalChange(index, "rtcChecked", true);
        } else if (isNotRTC) {
          handleDrawalChange(index, "rtcChecked", false);
        }
      }, 0);
    }

    if (field === "slotStartAt" && value && expiryDate && expiryTime) {
      const currentRow = finalModalEditData.slotDrawls[index];
      const updatedRow = { ...currentRow, [field]: value };

      if (updatedRow.dateStartAt) {
        const startDateOnly = new Date(updatedRow.dateStartAt);
        const expiryDateOnly = new Date(expiryDate);
        startDateOnly.setHours(0, 0, 0, 0);
        expiryDateOnly.setHours(0, 0, 0, 0);

        if (startDateOnly.getTime() === expiryDateOnly.getTime()) {
          const startTimeInMinutes = timeToMinutes(value);
          const expiryTimeInMinutes = timeToMinutes(expiryTime);
          if (
            startTimeInMinutes !== null &&
            expiryTimeInMinutes !== null &&
            startTimeInMinutes <= expiryTimeInMinutes
          ) {
            setErrors((errors) => ({
              ...errors,
              drawalRows: [
                ...(errors.drawalRows || []).slice(0, index),
                {
                  ...(errors.drawalRows?.[index] || {}),
                  slotStartAt:
                    "Start Time must be 3hrs after listing expiry time",
                },
                ...(errors.drawalRows || []).slice(index + 1),
              ],
            }));
          }
        }
      }
    }

    if (field === "dateStartAt" && value && expiryDate && expiryTime) {
      const currentRow = finalModalEditData.slotDrawls[index];
      const updatedRow = { ...currentRow, [field]: value };

      if (updatedRow.slotStartAt) {
        const startDateOnly = new Date(value);
        const expiryDateOnly = new Date(expiryDate);
        startDateOnly.setHours(0, 0, 0, 0);
        expiryDateOnly.setHours(0, 0, 0, 0);

        if (startDateOnly.getTime() === expiryDateOnly.getTime()) {
          const startTimeInMinutes = timeToMinutes(updatedRow.slotStartAt);
          const expiryTimeInMinutes = timeToMinutes(expiryTime);
          if (
            startTimeInMinutes !== null &&
            expiryTimeInMinutes !== null &&
            startTimeInMinutes <= expiryTimeInMinutes
          ) {
            setErrors((errors) => ({
              ...errors,
              drawalRows: [
                ...(errors.drawalRows || []).slice(0, index),
                {
                  ...(errors.drawalRows?.[index] || {}),
                  slotStartAt:
                    "Start Time must be 3hrs after listing expiry time",
                },
                ...(errors.drawalRows || []).slice(index + 1),
              ],
            }));
          }
        }
      }
    }
  };

  const minTimeForStartTime = (row) => {
    if (!row.dateStartAt) return undefined;
    const startDateOnly = new Date(row.dateStartAt);
    startDateOnly.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (+startDateOnly === +today) {
      const dt = new Date(new Date().getTime() + 3 * 60 * 60 * 1000);
      const h = String(dt.getHours()).padStart(2, "0");
      const m = String(dt.getMinutes()).padStart(2, "0");
      return `${h}:${m}`;
    }
    return undefined;
  };

  const handleSubmit = () => {
    const newErrors = {
      injectionRows: [],
      drawalRows: [],
      bankingFactor: "",
      settlementRate: "",
      dateOverlap: "",
    };

    let hasErrors = false;
    const now = new Date();
    let listingExpiryDateTime = null;
    if (expiryDate && expiryTime) {
      listingExpiryDateTime = new Date(`${expiryDate}T${expiryTime}`);
    }

    const timeToMinutes = (t) => {
      if (!t) return null;
      const [h, m] = t.split(":").map(Number);
      return h * 60 + m;
    };
    const isTimeValid = (start, end) => {
      const s = timeToMinutes(start);
      const e = timeToMinutes(end);
      return s !== null && e !== null && e > s;
    };

    finalModalEditData.slotInjects.forEach((row, index) => {
      const rowErrors = {};
      if (!row.dateStartAt) {
        rowErrors.dateStartAt = "Start Date is required";
        hasErrors = true;
      }
      if (!row.dateEndAt) {
        rowErrors.dateEndAt = "End Date is required";
        hasErrors = true;
      }
      if (!row.slotStartAt) {
        rowErrors.slotStartAt = "Start Time is required";
        hasErrors = true;
      }
      if (!row.slotEndAt) {
        rowErrors.slotEndAt = "End Time is required";
        hasErrors = true;
      }
      if (!row.requiredQty) {
        rowErrors.requiredQty = "Quantity is required";
        hasErrors = true;
      }

      if (
        row.requiredQty &&
        (isNaN(Number(row.requiredQty)) ||
          Number(row.requiredQty) <= 0 ||
          Number(row.requiredQty) > 10000)
      ) {
        if (Number(row.requiredQty) > 10000) {
          rowErrors.requiredQty = "Quantity must be below 10,000";
        } else {
          rowErrors.requiredQty = "Quantity must be a positive number";
        }
        hasErrors = true;
      }
      if (
        row.slotStartAt &&
        row.slotEndAt &&
        !isTimeValid(row.slotStartAt, row.slotEndAt)
      ) {
        rowErrors.slotEndAt = "End Time must be later";
        hasErrors = true;
      }
      if (row.dateStartAt && listingExpiryDateTime) {
        const startDateOnly = new Date(row.dateStartAt);
        const expiryDateOnly = new Date(expiryDate);
        startDateOnly.setHours(0, 0, 0, 0);
        expiryDateOnly.setHours(0, 0, 0, 0);
        if (startDateOnly < expiryDateOnly) {
          rowErrors.dateStartAt =
            "Start Date must be on or after listing expiry date";
          hasErrors = true;
        }
        if (
          startDateOnly.getTime() === expiryDateOnly.getTime() &&
          row.slotStartAt &&
          expiryTime
        ) {
          const startTimeInMinutes = timeToMinutes(row.slotStartAt);
          const expiryTimeInMinutes = timeToMinutes(expiryTime);
          if (startTimeInMinutes <= expiryTimeInMinutes) {
            rowErrors.slotStartAt =
              "Start Time must be 3hrs after listing expiry time";
            hasErrors = true;
          }
        }
      }
      if (row.dateStartAt && row.slotStartAt) {
        const dt = new Date(`${row.dateStartAt}T${row.slotStartAt}`);
        if (!isNaN(dt)) {
          const diffHrsFromNow = (dt - now) / (1000 * 60 * 60);
          if (diffHrsFromNow < 3) {
            rowErrors.slotStartAt =
              "Start Time must be at least 3 hours from the current time";
            hasErrors = true;
          }
          if (
            listingExpiryDateTime &&
            !rowErrors.slotStartAt &&
            !rowErrors.dateStartAt
          ) {
            const timeDiffHours =
              (dt - listingExpiryDateTime) / (1000 * 60 * 60);
            if (timeDiffHours < 3) {
              rowErrors.slotStartAt =
                "Start Time must be at least 3 hours after listing expiry";
              hasErrors = true;
            }
          }
        }
      }
      newErrors.injectionRows[index] = rowErrors;
    });

    finalModalEditData.slotDrawls.forEach((row, index) => {
      const rowErrors = {};
      if (!row.dateStartAt) {
        rowErrors.dateStartAt = "Start Date is required";
        hasErrors = true;
      }
      if (!row.dateEndAt) {
        rowErrors.dateEndAt = "End Date is required";
        hasErrors = true;
      }
      if (!row.slotStartAt) {
        rowErrors.slotStartAt = "Start Time is required";
        hasErrors = true;
      }
      if (!row.slotEndAt) {
        rowErrors.slotEndAt = "End Time is required";
        hasErrors = true;
      }
      if (!row.requiredQty) {
        rowErrors.requiredQty = "Quantity is required";
        hasErrors = true;
      }

      if (
        row.requiredQty &&
        (isNaN(Number(row.requiredQty)) ||
          Number(row.requiredQty) <= 0 ||
          Number(row.requiredQty) > 10000)
      ) {
        if (Number(row.requiredQty) > 10000) {
          rowErrors.requiredQty = "Quantity must be below 10,000";
        } else {
          rowErrors.requiredQty = "Quantity must be a positive number";
        }
        hasErrors = true;
      }
      if (
        row.slotStartAt &&
        row.slotEndAt &&
        !isTimeValid(row.slotStartAt, row.slotEndAt)
      ) {
        rowErrors.slotEndAt = "End Time must be later";
        hasErrors = true;
      }
      if (row.dateStartAt && listingExpiryDateTime) {
        const startDateOnly = new Date(row.dateStartAt);
        const expiryDateOnly = new Date(expiryDate);
        startDateOnly.setHours(0, 0, 0, 0);
        expiryDateOnly.setHours(0, 0, 0, 0);
        if (startDateOnly < expiryDateOnly) {
          rowErrors.dateStartAt =
            "Start Date must be on or after listing expiry date";
          hasErrors = true;
        }
        if (
          startDateOnly.getTime() === expiryDateOnly.getTime() &&
          row.slotStartAt &&
          expiryTime
        ) {
          const startTimeInMinutes = timeToMinutes(row.slotStartAt);
          const expiryTimeInMinutes = timeToMinutes(expiryTime);
          if (startTimeInMinutes <= expiryTimeInMinutes) {
            rowErrors.slotStartAt =
              "Start Time must be 3hrs after listing expiry time";
            hasErrors = true;
          }
        }
      }
      if (row.dateStartAt && row.slotStartAt) {
        const dt = new Date(`${row.dateStartAt}T${row.slotStartAt}`);
        if (!isNaN(dt)) {
          const diffHrsFromNow = (dt - now) / (1000 * 60 * 60);
          if (diffHrsFromNow < 3) {
            rowErrors.slotStartAt =
              "Start Time must be at least 3 hours from the current time";
            hasErrors = true;
          }
          if (
            listingExpiryDateTime &&
            !rowErrors.slotStartAt &&
            !rowErrors.dateStartAt
          ) {
            const timeDiffHours =
              (dt - listingExpiryDateTime) / (1000 * 60 * 60);
            if (timeDiffHours < 3) {
              rowErrors.slotStartAt =
                "Start Time must be at least 3 hours after listing expiry";
              hasErrors = true;
            }
          }
        }
      }
      newErrors.drawalRows[index] = rowErrors;
    });

    if (
      !bankingFactor ||
      isNaN(Number(bankingFactor)) ||
      Number(bankingFactor) < 100
    ) {
      newErrors.bankingFactor = "Banking Factor must be greater than 100";
      hasErrors = true;
    }

    if (!settlementRate || !/^DAM[+-]\d+(\.\d{1,3})?$/.test(settlementRate)) {
      newErrors.settlementRate = "Settlement Rate required (e.g., DAM+1.234)";
    } else {
      const damMatch = settlementRate.match(/^DAM([+-])(\d+(?:\.\d{1,3})?)$/);
      if (damMatch) {
        const sign = damMatch[1];
        const value = parseFloat(damMatch[2]);
        const actualValue = sign === "+" ? value : -value;

        if (actualValue < -9 || actualValue > 9) {
          newErrors.settlementRate = "DAM value must be b/w DAM-9 & DAM+9";
        }
      }
    }

    const timeSlotOverlaps = validateInjectionDrawalSlotTimeOverlaps(
      finalModalEditData.slotInjects,
      finalModalEditData.slotDrawls,
    );

    if (timeSlotOverlaps.length > 0) {
      newErrors.dateOverlap = "Injection and drawal slots overlap";
      hasErrors = true;

      timeSlotOverlaps.forEach((ov) => {
        if (!newErrors.injectionRows[ov.injectSlotIndex]) {
          newErrors.injectionRows[ov.injectSlotIndex] = {};
        }
        newErrors.injectionRows[ov.injectSlotIndex].slotStartAt =
          `Overlaps with drawal slot`;
        newErrors.injectionRows[ov.injectSlotIndex].slotEndAt =
          `Overlaps with drawal slot`;

        if (!newErrors.drawalRows[ov.drawalSlotIndex]) {
          newErrors.drawalRows[ov.drawalSlotIndex] = {};
        }
        newErrors.drawalRows[ov.drawalSlotIndex].slotStartAt =
          `Overlaps with injection slot`;
        newErrors.drawalRows[ov.drawalSlotIndex].slotEndAt =
          `Overlaps with injection slot`;
      });
    } else {
      // Check for same-type overlaps (injection-to-injection)
      const injectionOverlaps = validateSameTypeOverlaps(
        finalModalEditData.slotInjects,
        "injection",
      );

      if (injectionOverlaps.length > 0) {
        hasErrors = true;
        injectionOverlaps.forEach((overlap) => {
          if (!newErrors.injectionRows[overlap.slotIndex1]) {
            newErrors.injectionRows[overlap.slotIndex1] = {};
          }
          if (!newErrors.injectionRows[overlap.slotIndex2]) {
            newErrors.injectionRows[overlap.slotIndex2] = {};
          }

          newErrors.injectionRows[overlap.slotIndex1].dateStartAt =
            "Overlaps with another slot";
          newErrors.injectionRows[overlap.slotIndex1].dateEndAt =
            "Overlaps with another slot";
          newErrors.injectionRows[overlap.slotIndex2].dateStartAt =
            "Overlaps with another slot";
          newErrors.injectionRows[overlap.slotIndex2].dateEndAt =
            "Overlaps with another slot";
        });
      }

      // Check for same-type overlaps (drawal-to-drawal)
      const drawalOverlaps = validateSameTypeOverlaps(
        finalModalEditData.slotDrawls,
        "drawal",
      );

      if (drawalOverlaps.length > 0) {
        hasErrors = true;
        drawalOverlaps.forEach((overlap) => {
          if (!newErrors.drawalRows[overlap.slotIndex1]) {
            newErrors.drawalRows[overlap.slotIndex1] = {};
          }
          if (!newErrors.drawalRows[overlap.slotIndex2]) {
            newErrors.drawalRows[overlap.slotIndex2] = {};
          }

          newErrors.drawalRows[overlap.slotIndex1].dateStartAt =
            "Overlaps with another slot";
          newErrors.drawalRows[overlap.slotIndex1].dateEndAt =
            "Overlaps with another slot";
          newErrors.drawalRows[overlap.slotIndex2].dateStartAt =
            "Overlaps with another slot";
          newErrors.drawalRows[overlap.slotIndex2].dateEndAt =
            "Overlaps with another slot";
        });
      }

      const dateOverlaps = validateDateOverlaps(
        finalModalEditData.slotInjects,
        finalModalEditData.slotDrawls,
      );
      if (dateOverlaps.length > 0) {
        let hasActualTimeConflict = false;

        dateOverlaps.forEach((overlap) => {
          const { injection, drawal } = overlap;

          const injectionSlot =
            finalModalEditData.slotInjects[injection.slotIndex];
          const drawalSlot = finalModalEditData.slotDrawls[drawal.slotIndex];

          const injectionDates = getDateRangeArray(
            injectionSlot.dateStartAt,
            injectionSlot.dateEndAt,
          );
          const drawalDates = getDateRangeArray(
            drawalSlot.dateStartAt,
            drawalSlot.dateEndAt,
          );
          const sharedDates = injectionDates.filter((date) =>
            drawalDates.includes(date),
          );

          if (sharedDates.length > 0) {
            const injStartTime = timeToMinutes(injectionSlot.slotStartAt);
            const injEndTime = timeToMinutes(injectionSlot.slotEndAt);
            const drawStartTime = timeToMinutes(drawalSlot.slotStartAt);
            const drawEndTime = timeToMinutes(drawalSlot.slotEndAt);

            if (injStartTime <= drawEndTime && drawStartTime <= injEndTime) {
              hasActualTimeConflict = true;
            }
          }
        });

        if (hasActualTimeConflict) {
          newErrors.dateOverlap = "Injection and drawal slots overlap";
          hasErrors = true;

          dateOverlaps.forEach((overlap) => {
            const { injection, drawal } = overlap;

            if (!newErrors.injectionRows[injection.slotIndex]) {
              newErrors.injectionRows[injection.slotIndex] = {};
            }
            newErrors.injectionRows[injection.slotIndex].dateStartAt =
              "Conflicts with drawal slot";
            newErrors.injectionRows[injection.slotIndex].dateEndAt =
              "Conflicts with drawal slot";

            if (!newErrors.drawalRows[drawal.slotIndex]) {
              newErrors.drawalRows[drawal.slotIndex] = {};
            }
            newErrors.drawalRows[drawal.slotIndex].dateStartAt =
              "Conflicts with injection slot";
            newErrors.drawalRows[drawal.slotIndex].dateEndAt =
              "Conflicts with injection slot";
          });
        }
      }
    }

    setErrors(newErrors);

    if (hasErrors) {
      return;
    }

    onSubmit({
      ...finalModalEditData,
      settlementRate,
      bankingFactor: Number(bankingFactor),
      expiryDate,
      expiryTime,
      termsExpiryDate,
      termsExpiryTime,
      activeType,
    });
  };

  const today = new Date();

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="child-modal-title"
      aria-describedby="child-modal-description"
    >
      <div className={`popupModal ${Styles.createEditModalCont}`}>
        <div className={`display_flex_row modalHeading ${Styles.headingText}`}>
          <div className={`headingText ${Styles.finalHead}`}>
            Draft Terms & Conditions: <span>{selfListingData?.name}</span>
          </div>
          <Close
            style={{ fontSize: "24px", color: "#777777", cursor: "pointer" }}
            onClick={handleClose}
          />
        </div>
        <div
          className={`scroll_bar_y display_flex_column_gap_8 ${Styles.bankingInputMainContainer}`}
        >
          {/* Injection/Import Range */}
          {finalModalEditData.slotInjects.map((row, index) => (
            <div key={index} className={Styles.editCard}>
              <div
                className={`display_flex_column_gap_4 ${Styles.injectDrawalCont1}`}
              >
                <span className={Styles.injectDrawalHead}>
                  Injection/Import Range {index + 1}
                </span>
              </div>

              <div
                className={`display_flex_row_common ${Styles.eachSelectionMainContainer}`}
              >
                <DateInput
                  heading="Start Date"
                  value={parseDateString(row.dateStartAt)}
                  minDate={expiryDate ? new Date(expiryDate) : today}
                  height={35}
                  onChange={(date) =>
                    handleInjectionChangeWithValidation(
                      index,
                      "dateStartAt",
                      formatDateToDDMMYYYY(date),
                    )
                  }
                  maxDate={
                    row.dateEndAt ? parseDateString(row.dateEndAt) : undefined
                  }
                  error={errors.injectionRows?.[index]?.dateStartAt}
                  errorMessage={errors.injectionRows?.[index]?.dateStartAt}
                />
                <DateInput
                  heading="End Date"
                  value={parseDateString(row.dateEndAt)}
                  minDate={
                    row.dateStartAt ? parseDateString(row.dateStartAt) : today
                  }
                  height={35}
                  onChange={(date) =>
                    handleInjectionChangeWithValidation(
                      index,
                      "dateEndAt",
                      formatDateToDDMMYYYY(date),
                    )
                  }
                  error={errors.injectionRows?.[index]?.dateEndAt}
                  errorMessage={errors.injectionRows?.[index]?.dateEndAt}
                />
                <TimeInput
                  type="startTime"
                  heading="Start Time"
                  value={displayTime(row.slotStartAt)}
                  rtcButton={true}
                  height={35}
                  minTime={minTimeForStartTime(row)}
                  maxTime={row.slotEndAt}
                  rtcChecked={row.rtcChecked}
                  disabled={row.rtcChecked}
                  onChange={(value) =>
                    handleInjectionChangeWithValidation(
                      index,
                      "slotStartAt",
                      normalizeTime(value),
                    )
                  }
                  handleRTC={(value) => {
                    handleInjectionChangeWithValidation(
                      index,
                      "slotEndAt",
                      normalizeTime(value),
                    );
                    handleInjectionChangeWithValidation(
                      index,
                      "rtcChecked",
                      true,
                    );

                    if (value === "24:00") {
                      handleInjectionChangeWithValidation(
                        index,
                        "slotStartAt",
                        "00:00",
                      );
                    } else {
                      handleInjectionChangeWithValidation(
                        index,
                        "slotStartAt",
                        "",
                      );
                    }
                  }}
                  error={errors.injectionRows?.[index]?.slotStartAt}
                  errorMessage={errors.injectionRows?.[index]?.slotStartAt}
                />
                <TimeInput
                  type="endTime"
                  heading="End Time"
                  value={displayTime(row.slotEndAt)}
                  height={35}
                  minTime={row.slotStartAt}
                  maxTime="23:45"
                  minutesStep={15}
                  disabled={row.rtcChecked}
                  onChange={(value) =>
                    handleInjectionChangeWithValidation(
                      index,
                      "slotEndAt",
                      normalizeTime(value),
                    )
                  }
                  error={errors.injectionRows?.[index]?.slotEndAt}
                  errorMessage={errors.injectionRows?.[index]?.slotEndAt}
                />
                <TextInput
                  heading="Quantity (MW)"
                  value={row.requiredQty != null ? String(row.requiredQty) : ""}
                  height={35}
                  onChange={(val) => {
                    // Allow empty string, integers, and decimals with up to 2 decimal places
                    if (/^(\d*\.?\d{0,2})?$/.test(val)) {
                      handleInjectionChangeWithValidation(
                        index,
                        "requiredQty",
                        val,
                      );
                    }
                  }}
                  error={errors.injectionRows?.[index]?.requiredQty}
                  errorMessage={errors.injectionRows?.[index]?.requiredQty}
                />

                <div
                  className={`display_flex_column_gap_8 ${Styles.alterContainer}`}
                >
                  {finalModalEditData.slotInjects.length > 1 && (
                    <DeleteIcon
                      onClick={() => removeInjectionRow(index)}
                      style={{ cursor: "pointer" }}
                    />
                  )}

                  <AddIcon
                    onClick={addInjectionRow}
                    style={{ cursor: "pointer" }}
                  />
                </div>
              </div>
            </div>
          ))}

          {/* Drawal/Export Range */}
          {finalModalEditData.slotDrawls.map((row, index) => (
            <div key={index} className={Styles.editCard}>
              <div
                className={`display_flex_column_gap_4 ${Styles.injectDrawalCont1}`}
              >
                <span className={Styles.injectDrawalHead}>
                  Drawal/Export Range {index + 1}
                </span>
              </div>

              <div
                className={`display_flex_row_common ${Styles.eachSelectionMainContainer}`}
              >
                <DateInput
                  heading="Start Date"
                  value={parseDateString(row.dateStartAt)}
                  minDate={expiryDate ? new Date(expiryDate) : today}
                  height={35}
                  onChange={(date) =>
                    handleDrawalChangeWithValidation(
                      index,
                      "dateStartAt",
                      formatDateToDDMMYYYY(date),
                    )
                  }
                  maxDate={
                    row.dateEndAt ? parseDateString(row.dateEndAt) : undefined
                  }
                  error={errors.drawalRows?.[index]?.dateStartAt}
                  errorMessage={errors.drawalRows?.[index]?.dateStartAt}
                />
                <DateInput
                  heading="End Date"
                  value={parseDateString(row.dateEndAt)}
                  minDate={
                    row.dateStartAt ? parseDateString(row.dateStartAt) : today
                  }
                  height={35}
                  onChange={(date) =>
                    handleDrawalChangeWithValidation(
                      index,
                      "dateEndAt",
                      formatDateToDDMMYYYY(date),
                    )
                  }
                  error={errors.drawalRows?.[index]?.dateEndAt}
                  errorMessage={errors.drawalRows?.[index]?.dateEndAt}
                />
                <TimeInput
                  type="startTime"
                  heading="Start Time"
                  value={displayTime(row.slotStartAt)}
                  height={35}
                  rtcButton={true}
                  minTime={minTimeForStartTime(row)}
                  maxTime={row.slotEndAt}
                  rtcChecked={row.rtcChecked}
                  disabled={row.rtcChecked}
                  onChange={(value) =>
                    handleDrawalChangeWithValidation(
                      index,
                      "slotStartAt",
                      normalizeTime(value),
                    )
                  }
                  handleRTC={(value) => {
                    handleDrawalChangeWithValidation(
                      index,
                      "slotEndAt",
                      normalizeTime(value),
                    );
                    handleDrawalChangeWithValidation(index, "rtcChecked", true);

                    if (value === "24:00") {
                      handleDrawalChangeWithValidation(
                        index,
                        "slotStartAt",
                        "00:00",
                      );
                    } else {
                      handleDrawalChangeWithValidation(
                        index,
                        "slotStartAt",
                        "",
                      );
                    }
                  }}
                  error={errors.drawalRows?.[index]?.slotStartAt}
                  errorMessage={errors.drawalRows?.[index]?.slotStartAt}
                />
                <TimeInput
                  type="endTime"
                  heading="End Time"
                  value={displayTime(row.slotEndAt)}
                  height={35}
                  minTime={row.slotStartAt}
                  maxTime="23:45"
                  minutesStep={15}
                  disabled={row.rtcChecked}
                  onChange={(value) =>
                    handleDrawalChangeWithValidation(
                      index,
                      "slotEndAt",
                      normalizeTime(value),
                    )
                  }
                  error={errors.drawalRows?.[index]?.slotEndAt}
                  errorMessage={errors.drawalRows?.[index]?.slotEndAt}
                />
                <TextInput
                  heading="Quantity (MW)"
                  value={row.requiredQty != null ? String(row.requiredQty) : ""}
                  height={35}
                  onChange={(val) => {
                    if (/^(\d*\.?\d{0,2})?$/.test(val)) {
                      handleDrawalChangeWithValidation(
                        index,
                        "requiredQty",
                        val,
                      );
                    }
                  }}
                  error={errors.drawalRows?.[index]?.requiredQty}
                  errorMessage={errors.drawalRows?.[index]?.requiredQty}
                />
                <div
                  className={`display_flex_column_gap_8 ${Styles.alterContainer}`}
                >
                  {finalModalEditData.slotDrawls.length > 1 && (
                    <DeleteIcon
                      onClick={() => removeDrawalRow(index)}
                      style={{ cursor: "pointer" }}
                    />
                  )}

                  <AddIcon
                    onClick={addDrawalRow}
                    style={{ cursor: "pointer" }}
                  />
                </div>
              </div>
            </div>
          ))}

          {/* Banking Factor and Settlement Rate */}
          <div
            className={`display_flex_row_common ${Styles.eachSelectionMainContainer}`}
          >
            <TextInput
              heading="Banking Factor (%)"
              value={bankingFactor}
              height={35}
              onChange={handleBankingFactorChange}
              error={errors.bankingFactor}
              errorMessage={errors.bankingFactor}
            />
            <TextInput
              heading="Settlement Rate (₹/kWh)"
              value={settlementRate}
              height={35}
              onChange={handleSettlementRate}
              error={errors.settlementRate}
              errorMessage={errors.settlementRate}
            />
            <div className={Styles.bankingTypeContainer}>
              <div className={Styles.typeHead}>Type</div>
              <div className={Styles.bankTypeSub}>
                {["GNA", "TGNA", "EITHER"].map((type) => (
                  <div
                    key={type}
                    className={`${Styles.firstType} ${
                      activeType === type ? Styles.active : ""
                    }`}
                    onClick={() => setActiveType(type)}
                  >
                    {type}
                  </div>
                ))}
              </div>
            </div>
          </div>
          {/* Buttons */}
        </div>
        <div className={Styles.buttonsContainerFinal}>
          <div onClick={handleClose} style={{ cursor: "pointer" }}>
            CANCEL
          </div>
          <div onClick={handleSubmit} style={{ cursor: "pointer" }}>
            SUBMIT
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default FinalEditModal;
